import React from 'react';
import { CheckCircle, XCircle, MinusCircle, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import './ChatComponents.css';

/**
 * Competitive comparison matrix with visual indicators
 */
function ComparisonMatrix({ title, data }) {
  const { capability, paloAlto, competitors } = data;

  const getCoverageIcon = (level) => {
    switch (level) {
      case 'full':
        return <CheckCircle size={16} color="var(--color-success)" />;
      case 'partial':
        return <MinusCircle size={16} color="var(--color-warning)" />;
      case 'basic':
      case 'none':
        return <XCircle size={16} color="var(--color-error)" />;
      default:
        return <MinusCircle size={16} color="var(--color-text-muted)" />;
    }
  };

  const getVsIndicator = (vs) => {
    switch (vs) {
      case 'stronger':
        return { icon: <ArrowUp size={14} />, color: 'var(--color-error)', label: 'Competitor stronger' };
      case 'weaker':
        return { icon: <ArrowDown size={14} />, color: 'var(--color-success)', label: 'PA stronger' };
      case 'equivalent':
        return { icon: <Minus size={14} />, color: 'var(--color-warning)', label: 'Equivalent' };
      default:
        return { icon: <Minus size={14} />, color: 'var(--color-text-muted)', label: vs };
    }
  };

  return (
    <div className="cc-comparison">
      {title && <div className="cc-section-title">{title}</div>}

      {/* Capability info */}
      {capability && (
        <div className="cc-comparison-header">
          <span className="cc-cap-name">{capability.name}</span>
          {capability.pillar && <span className="cc-cap-pillar">{capability.pillar}</span>}
        </div>
      )}

      <div className="cc-comparison-grid">
        {/* Palo Alto column */}
        <div className="cc-comparison-col cc-comparison-pa">
          <div className="cc-col-header">
            <span className="cc-vendor-name">Palo Alto</span>
            {getCoverageIcon(paloAlto?.bestCoverage)}
          </div>
          <div className="cc-col-products">
            {paloAlto?.products?.map((p, idx) => (
              <div key={idx} className="cc-comp-product">
                <span className="cc-product-name">{p.product_name || p.productId}</span>
                <span className={`cc-coverage cc-coverage-${p.coverage_level}`}>
                  {p.coverage_level}
                </span>
                {p.license_tier && (
                  <span className="cc-license">{p.license_tier}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Competitors */}
        {competitors?.map((comp, idx) => {
          const vs = getVsIndicator(comp.vsPaloAlto);

          return (
            <div key={idx} className="cc-comparison-col">
              <div className="cc-col-header">
                <span className="cc-vendor-name">{comp.name}</span>
                {getCoverageIcon(comp.coverageLevel)}
              </div>
              <div className="cc-comp-product">
                <span className="cc-product-name">{comp.product}</span>
                <span className={`cc-coverage cc-coverage-${comp.coverageLevel}`}>
                  {comp.coverageLevel}
                </span>
              </div>
              <div className="cc-vs-indicator" style={{ color: vs.color }}>
                {vs.icon}
                <span>{vs.label}</span>
              </div>
              {comp.comparisonNotes && (
                <div className="cc-comp-notes">{comp.comparisonNotes}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ComparisonMatrix;
